import {
  Children,
  useRef,
  useEffect,
  useReducer,
  useCallback,
  cloneElement,
} from "react";
import classnames from "classnames";
import { useIntersectionObserver } from "@/hooks";
import DropdownMenu from "@/components/DropdownMenu";
import styles from "@/styles/OverflowMenu.module.scss";
import dropDownStyles from "@/styles/DropdownMenu.module.scss";
import IconEllipsis from "../svgs/icon_ellipsis.svg";

function MenuItem({ index, intersectCallback, root, children }) {
  const item = useRef();
  const opts = {
    root: root,
    rootMargin: "3px",
    threshold: 1.0,
  };
  const intersect = useIntersectionObserver(item, opts);

  useEffect(() => {
    intersectCallback(intersect, index);
  }, [intersect]);

  return (
    <li
      className={classnames(styles.item, { [styles.intersect]: intersect })}
      ref={item}
    >
      <div className={styles.itemInner}>{children}</div>
    </li>
  );
}

export default function OverflowMenu({ className, children }) {
  const root = useRef();
  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case "add":
        return [...state, action.index];
      case "remove":
        return state.filter((idx) => idx != action.index);
    }
  }, []);
  const callback = useCallback((intersect, index) => {
    if (intersect) {
      dispatch({ type: "remove", index });
    } else {
      dispatch({ type: "add", index });
    }
  }, []);

  return (
    <div className={classnames(styles.container, className)}>
      <ul className={styles.menu} ref={root}>
        {Children.map(children, (child, i) => (
          <MenuItem
            key={`menu-item-${i}`}
            index={i}
            intersectCallback={callback}
            root={root.current}
          >
            {child}
          </MenuItem>
        ))}
      </ul>
      <div
        className={classnames(styles.dropdown, {
          [styles.show]: state.length > 0,
        })}
      >
        <DropdownMenu
          button={<IconEllipsis />}
          scopedClass={dropDownStyles.forOverflowMenu}
          mouseOver
          right
        >
          <div className={dropDownStyles.item}>
            {state.sort().map((idx) => {
              const element = Children.toArray(children)[idx];
              if (!element) return null;
              return cloneElement(element, {
                key: `overflow-dropdown-menu-${idx}`,
              });
            })}
          </div>
        </DropdownMenu>
      </div>
    </div>
  );
}
